import {
  PrdDeptRoles,
  PrdReportRoles,
  PrdViewRoles,
  QcViewRoles,
  Role,
  Sale1010ViewRoles,
  Sale1020ViewRoles,
  Sale1030Roles,
  Sale2215ViewRoles,
  SaleForecastRoles,
  SaleViewRoles,
  SaleViewWo1010Roles,
  SupervisorRoles,
} from '@/acl'
import { getAllowedRolesForModulePath } from '@/acl/menu-allowed-roles'
import { PlnModifyRoles, PlnReportRoles, PlnViewRoles } from '@/acl/planning'
import {
  PoProblemViewRoles,
  PurReportRoles,
  PurViewRoles,
} from '@/acl/purchase'
import {
  Inv3010ViewRoles,
  Inv3020ViewRoles,
  Inv3510ViewRoles,
  WarMovementViewRoles,
  WarReportRoles,
  WarViewRoles,
} from '@/acl/warehouse'
import {
  IconBox,
  IconBuildingFactory,
  IconCalendar,
  IconReportSearch,
  IconShoppingBag,
  IconShoppingCart,
} from '@tabler/icons-react'

export type MenuItemType = {
  type: 'item'
  code: string
  label: string
  path: string
  leftSection?: React.ReactNode
  roles?: Role[] // deprecated, use allowedRoles from JSON instead
}

export type AllowedRoleAccess = 'R' | 'W'
export type AllowedRoleTuple = [Role, AllowedRoleAccess]

// Runtime-only enrichment (sourced from src/acl/menu-allowed-roles.json)
export type MenuItemWithAllowedRoles = MenuItemType & {
  allowedRoles?: AllowedRoleTuple[]
}

export type MenuSection = {
  type: 'section'
  label?: string
  leftSection?: React.ReactNode
  menus: MenuItemWithAllowedRoles[]
  column?: 'left' | 'middle' | 'right'
}

export type MenuGroup = {
  type: 'group'
  label: string
  /**
   * Module name used for ACL lookup in menu-allowed-roles.json.
   * Must match the module prefix in JSON keys (e.g., "ขาย (Sales)", "คลังสินค้า (Warehouse)").
   */
  module: string
  leftSection?: React.ReactNode
  sections: MenuSection[]
  /**
   * Layout style for dropdown menu:
   * - 'single': Simple single column layout (all sections stacked vertically)
   * - 'dynamic-two-column': Dynamic 2-column layout with complex pairing logic
   * - 'two-column': Fixed 2-column layout (left/right columns based on section.column)
   * - 'three-column': Fixed 3-column layout (left/middle/right columns based on section.column)
   * @default 'two-column'
   */
  layoutType?: 'single' | 'dynamic-two-column' | 'two-column' | 'three-column'
}

export type Menus = MenuGroup[]

/**
 * Hydrates menu items with module-specific allowed roles from JSON.
 * Each menu group uses its own module name to look up roles,
 * ensuring menu visibility is based on module-specific permissions.
 */
function hydrateAllowedRolesFromJson(menus: Menus): Menus {
  return menus.map((group) => ({
    ...group,
    sections: group.sections.map((section) => ({
      ...section,
      menus: section.menus.map((item) => ({
        ...item,
        allowedRoles: getAllowedRolesForModulePath(group.module, item.path),
      })),
    })),
  }))
}

const saleMenus: MenuGroup = {
  type: 'group',
  label: 'ขาย',
  module: 'ขาย (Sales)',
  leftSection: <IconShoppingBag size="1rem" stroke={1.5} color="white" />,
  sections: [
    {
      type: 'section',
      label: 'ใบแจ้งส่งสินค้า',
      menus: [
        {
          type: 'item',
          code: 'sale1010',
          label: 'sale1010 ใบแจ้งส่งสินค้า (Delivery Order)',
          path: '/sale/sale1010',
          roles: Sale1010ViewRoles,
        },
        {
          type: 'item',
          code: 'sale1020',
          label: 'sale1020 แพลนส่งสินค้า',
          path: '/sale/sale1020',
          roles: Sale1020ViewRoles,
        },
        {
          type: 'item',
          code: 'sale1030',
          label: 'sale1030 การติดตามใบแจ้งส่งสินค้า',
          path: '/sale/sale1030',
          roles: Sale1030Roles,
        },
      ],
    },
    {
      type: 'section',
      label: 'ใบสั่งงาน',
      menus: [
        {
          type: 'item',
          code: 'wo1010',
          label: 'wo1010 ใบสั่งงาน (Job Order)',
          path: '/production/wo1010',
          roles: SaleViewWo1010Roles,
        },
      ],
    },
    {
      type: 'section',
      label: 'Forecast',
      menus: [
        {
          type: 'item',
          code: 'sale1210',
          label: 'sale1210 นำเข้า Sales Forecast Excel file',
          path: '/sale/sale1210',
          roles: [...SaleForecastRoles, Role.QSS_OFFICER],
        },
        {
          type: 'item',
          code: 'sale1220',
          label: 'sale1220 จัดการรหัสสินค้าของลูกค้า',
          path: '/sale/sale1220',
          roles: [...SaleForecastRoles, Role.QSS_OFFICER],
        },
        {
          type: 'item',
          code: 'sale1230',
          label: 'sale1230 Material Forecast',
          path: '/sale/sale1230',
          roles: [...SaleForecastRoles, Role.QSS_OFFICER],
        },
      ],
    },
    {
      type: 'section',
      label: 'เบิก',
      menus: [
        {
          type: 'item',
          code: 'inv2215',
          label: 'inv2215 เบิก RM/FG เพื่อทดสอบ/แจก',
          path: '/warehouse/inv2215',
          roles: Sale2215ViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'สินค้ามีปัญหา',
      menus: [
        {
          type: 'item',
          code: 'sale2310',
          label: 'sale2310 แจ้งสินค้ามีปัญหา',
          path: '/sale/sale2310',
          roles: SaleViewRoles,
        },
      ],
    },
  ],
}

const planningMenus: MenuGroup = {
  type: 'group',
  label: 'วางแผน',
  module: 'วางแผน (Planning)',
  leftSection: <IconCalendar size="1rem" stroke={1.5} color="white" />,
  sections: [
    {
      type: 'section',
      label: 'วางแผนและติดตาม',
      menus: [
        {
          type: 'item',
          code: 'wo1110',
          label: 'wo1110 แผนการผลิตประจำสัปดาห์',
          path: '/production/wo1110',
          roles: PlnViewRoles,
        },
        {
          type: 'item',
          code: 'wo1115',
          label: 'wo1115 ตารางวางแผนการผลิต',
          path: '/production/wo1115',
          roles: PlnViewRoles,
        },
        {
          type: 'item',
          code: 'wo1210',
          label: 'wo1210 ติดตามใบหยิบ RM เพื่อการผลิต',
          path: '/production/wo1210',
          roles: PlnViewRoles,
        },
        {
          type: 'item',
          code: 'qc8015',
          label: 'qc8015 รับทราบใบกักกันวัตถุดิบเพื่อแผนการผลิต',
          path: '/qc/qc8015',
          roles: [...PlnModifyRoles, Role.QSS_OFFICER],
        },
      ],
    },
    {
      type: 'section',
      label: 'สอบถาม',
      menus: [
        {
          type: 'item',
          code: 'prd4010',
          label: 'prd4010 สอบถามการผลิต',
          path: '/production/prd4010',
          roles: PrdViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'BOM',
      menus: [
        {
          type: 'item',
          code: 'wo3010',
          label: 'wo3010 รายการ BOM',
          path: '/production/wo3010',
          roles: [...PlnModifyRoles, Role.QSS_OFFICER],
        },
      ],
    },
    {
      type: 'section',
      label: 'รายงาน',
      menus: [
        {
          type: 'item',
          code: 'wo9010',
          label: 'wo9010 รายงาน Backlog การผลิต',
          path: '/production/wo9010',
          roles: PlnReportRoles,
        },
      ],
    },
  ],
}

const purchaseMenus: MenuGroup = {
  type: 'group',
  label: 'จัดซื้อ',
  module: 'จัดซื้อ (Purchase)',
  leftSection: <IconShoppingCart size="1rem" stroke={1.5} color="white" />,
  sections: [
    {
      type: 'section',
      label: 'ใบสั่งซื้อ',
      menus: [
        {
          type: 'item',
          code: 'po1010',
          label: 'po1010 ใบสั่งซื้อ',
          path: '/purchase/po1010',
          roles: [Role.ADMIN, Role.ADMIN_CKD],
        },
      ],
    },
    {
      type: 'section',
      label: 'งานสั่งซื้อ',
      menus: [
        {
          type: 'item',
          code: 'PoTracking',
          label: 'PoTracking ติดตามใบสั่งซื้อ',
          path: '/purchase/tracking',
          roles: PurViewRoles,
        },
        {
          type: 'item',
          code: 'PO_Problem',
          label: 'PO Problem สรุปปัญหาการสั่งซื้อจาก Supplier',
          path: '/purchase/problem',
          roles: PoProblemViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'สอบถาม',
      menus: [
        {
          type: 'item',
          code: 'inv4310',
          label: 'inv4310 สอบถาม RM/FG ที่ต้องส่งคืน/รับทดแทน',
          path: '/warehouse/inv4310',
          roles: PurViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'รายงาน',
      menus: [
        {
          type: 'item',
          code: 'po9010',
          label: 'po9010 รายงาน RM/FG ค้างรับจากการสั่งซื้อ',
          path: '/purchase/po9010',
          roles: PurReportRoles,
        },
        {
          type: 'item',
          code: 'po9020',
          label: 'po9020 รายงาน Supplier ส่งวัตถุดิบล่าช้า',
          path: '/purchase/po9020',
          roles: PurReportRoles,
        },
        {
          type: 'item',
          code: 'po9030',
          label: 'po9030 รายงานสรุปยอดซื้อ',
          path: '/purchase/po9030',
          roles: [Role.PURCHASE_SUPERVISOR, Role.ACCOUNTING_SUPERVISOR],
        },
      ],
    },
  ],
}

const warehouseMenus: MenuGroup = {
  type: 'group',
  label: 'คลังสินค้า',
  module: 'คลังสินค้า (Warehouse)',
  leftSection: <IconBox size="1rem" stroke={1.5} color="white" />,
  layoutType: 'three-column',
  sections: [
    {
      type: 'section',
      label: 'รับ',
      column: 'left',
      menus: [
        {
          type: 'item',
          code: 'inv1010',
          label: 'inv1010 รับ RM/FG จากการสั่งซื้อ',
          path: '/warehouse/inv1010',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv2010',
          label: 'inv2010 รับ FG จากการสั่งผลิต',
          path: '/warehouse/inv2010',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv2310',
          label: 'inv2310 รับคืน FG จากลูกค้า',
          path: '/warehouse/inv2310',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'Inv1015',
          label: 'Inv1015 รับ RM/FG เพื่อทดสอบ',
          path: '/warehouse/inv1015',
          roles: WarViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'เบิก',
      column: 'left',
      menus: [
        {
          type: 'item',
          code: 'inv1210',
          label: 'inv1210 เบิก RM เพื่อผลิต',
          path: '/warehouse/inv1210',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv2210',
          label: 'inv2210 เบิก FG เพื่อจัดส่ง',
          path: '/warehouse/inv2210',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv2215',
          label: 'inv2215 เบิก RM/FG เพื่อทดสอบ/แจก',
          path: '/warehouse/inv2215',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv1310',
          label: 'inv1310 เบิก RM/FG เพื่อส่งคืน',
          path: '/warehouse/inv1310',
          roles: WarViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'เคลื่อนย้าย',
      column: 'middle',
      menus: [
        {
          type: 'item',
          code: 'inv1110',
          label: 'inv1110 จัดเก็บ RM/FG เข้าพื้นที่จัดเก็บ',
          path: '/warehouse/inv1110',
          roles: WarMovementViewRoles,
        },
        {
          type: 'item',
          code: 'inv1120',
          label: 'inv1120 จัดเก็บ RM/FG ไม่ผ่าน QC, รอส่งคืน',
          path: '/warehouse/inv1120',
          roles: WarMovementViewRoles,
        },
        {
          type: 'item',
          code: 'inv1140',
          label: 'inv1140 เคลื่อนย้ายตำแหน่งเก็บ RM/FG',
          path: '/warehouse/inv1140',
          roles: WarMovementViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'ปรับปรุง',
      column: 'middle',
      menus: [
        {
          type: 'item',
          code: 'inv3010',
          label: 'inv3010 ปรับปรุงจำนวนคงเหลือ',
          path: '/warehouse/inv3010',
          roles: Inv3010ViewRoles,
        },
        {
          type: 'item',
          code: 'inv3020',
          label: 'inv3020 บันทึกยอดยกมา',
          path: '/warehouse/inv3020',
          roles: Inv3020ViewRoles,
        },
        {
          type: 'item',
          code: 'inv3510',
          label: 'inv3510 การนับสต็อกแบบแบ่งโซน',
          path: '/warehouse/inv3510',
          roles: Inv3510ViewRoles,
        },
        {
          type: 'item',
          code: 'inv5010',
          label: 'inv5010 การนับสต็อก Cycle Count',
          path: '/warehouse/inv5010',
          roles: WarViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'สอบถาม',
      column: 'right',
      menus: [
        {
          type: 'item',
          code: 'inv4010',
          label: 'inv4010 สอบถามจำนวนคงเหลือ RM',
          path: '/warehouse/inv4010',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv4015',
          label: 'inv4015 สอบถามการหยิบ',
          path: '/warehouse/inv4015',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv4020',
          label: 'inv4020 สอบถามจำนวนคงเหลือ ตามตำแหน่งเก็บ',
          path: '/warehouse/inv4020',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv4110',
          label: 'inv4110 สอบถามจำนวนคงเหลือ FG',
          path: '/warehouse/inv4110',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv4210',
          label: 'inv4210 สอบถามรายการเคลื่อนไหว',
          path: '/warehouse/inv4210',
          roles: WarViewRoles,
        },
        {
          type: 'item',
          code: 'inv4310',
          label: 'inv4310 สอบถาม RM/FG ที่ต้องส่งคืน/รับทดแทน',
          path: '/warehouse/inv4310',
          roles: WarViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'รายงาน',
      column: 'right',
      menus: [
        {
          type: 'item',
          code: 'inv9010',
          label: 'inv9010 รายงานวัตถุดิบ (RM) คงเหลือในคลัง',
          path: '/warehouse/inv9010',
          roles: WarReportRoles,
        },
        {
          type: 'item',
          code: 'inv9020',
          label: 'inv9020 รายงานสินค้า (FG) คงเหลือในคลัง',
          path: '/warehouse/inv9020',
          roles: WarReportRoles,
        },
        {
          type: 'item',
          code: 'inv9110',
          label: 'inv9110 รายงานรับ RM/FG เข้าคลัง',
          path: '/warehouse/inv9110',
          roles: WarReportRoles,
        },
        {
          type: 'item',
          code: 'inv9120',
          label: 'inv9120 รายงานเบิก RM/FG ออกจากคลัง',
          path: '/warehouse/inv9120',
          roles: WarReportRoles,
        },
        {
          type: 'item',
          code: 'inv9210',
          label: 'inv9210 รายงาน Inventory Reject',
          path: '/warehouse/inv9210',
          roles: WarReportRoles,
        },
      ],
    },
  ],
}

const qcMenus: MenuGroup = {
  type: 'group',
  label: 'QC',
  module: 'QC (Quality Control)',
  leftSection: <IconReportSearch size="1rem" stroke={1.5} color="white" />,
  sections: [
    {
      type: 'section',
      label: 'ตรวจสอบ',
      menus: [
        {
          type: 'item',
          code: 'qc1010',
          label: 'qc1010 ตรวจสอบ RM/FG จากการสั่งซื้อ/รับคืน',
          path: '/qc/qc1010',
          roles: QcViewRoles,
        },
        {
          type: 'item',
          code: 'qc8010',
          label: 'qc8010 การกักกันวัตถุดิบ/สินค้า',
          path: '/qc/qc8010',
          roles: QcViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'เบิก',
      menus: [
        {
          type: 'item',
          code: 'qc8110',
          label: 'qc8110 การเบิก FG ระหว่างการบรรจุ เพื่อทดสอบ',
          path: '/qc/qc8110',
          roles: QcViewRoles,
        },
        {
          type: 'item',
          code: 'inv2215',
          label: 'inv2215 เบิก RM/FG เพื่อทดสอบ/แจก',
          path: '/warehouse/inv2215',
          roles: QcViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'งานคัดแยก / ซ่อมแซม',
      menus: [
        {
          type: 'item',
          code: 'qc8030',
          label: 'qc8030 การบันทึกผลการคัดหรือซ่อมแซม',
          path: '/qc/qc8030',
          roles: QcViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'รายงาน',
      menus: [
        {
          type: 'item',
          code: 'qc9010',
          label: 'qc9010 รายงานการกักกันสินค้า',
          path: '/qc/qc9010',
          roles: [...SupervisorRoles, Role.QSS_OFFICER],
        },
      ],
    },
  ],
}

const productionMenus: MenuGroup = {
  type: 'group',
  label: 'การผลิต',
  module: 'การผลิต (Production)',
  leftSection: <IconBuildingFactory size="1rem" stroke={1.5} color="white" />,
  sections: [
    {
      type: 'section',
      label: 'งานผลิต',
      menus: [
        {
          type: 'item',
          code: 'prd1210',
          label: 'prd1210 ยืนยันการรับวัตถุดิบเพื่อการผลิต',
          path: '/production/prd1210',
          roles: PrdViewRoles,
        },
        {
          type: 'item',
          code: 'prd1010',
          label: 'prd1010 บันทึกผลการผลิต',
          path: '/production/prd1010',
          roles: PrdViewRoles,
        },
        {
          type: 'item',
          code: 'prd1015',
          label: 'prd1015 สถานะการผลิต',
          path: '/production/prd1015',
          roles: PrdViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'งานบรรจุ',
      menus: [
        {
          type: 'item',
          code: 'prd1110',
          label: 'prd1110 บันทึกผลการบรรจุสินค้า',
          path: '/production/prd1110',
          roles: PrdViewRoles,
        },
        {
          type: 'item',
          code: 'prd2010',
          label: 'prd2010 บันทึกส่ง FG จากการผลิตเข้าคลัง',
          path: '/production/prd2010',
          roles: PrdViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'งานคัดแยก / ซ่อมแซม',
      menus: [
        {
          type: 'item',
          code: 'qc8030',
          label: 'qc8030 การบันทึกผลการคัดหรือซ่อมแซม',
          path: '/qc/qc8030',
          roles: PrdViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'งานของเสีย',
      menus: [
        {
          type: 'item',
          code: 'prd3010',
          label: 'prd3010 บันทึกสรุปของเสีย',
          path: '/production/prd3010',
          roles: [...PrdDeptRoles, Role.QSS_OFFICER],
        },
        {
          type: 'item',
          code: 'prd3110',
          label: 'prd3110 สรุปของเสียประจำเดือน',
          path: '/production/prd3110',
          roles: [...PrdDeptRoles, Role.QSS_OFFICER],
        },
      ],
    },
    {
      type: 'section',
      label: 'สอบถาม',
      column: 'right',
      menus: [
        {
          type: 'item',
          code: 'prd4010',
          label: 'prd4010 สอบถามการผลิต',
          path: '/production/prd4010',
          roles: PrdViewRoles,
        },
      ],
    },
    {
      type: 'section',
      label: 'รายงาน',
      column: 'right',
      menus: [
        {
          type: 'item',
          code: 'prd4020',
          label: 'prd4020 รายงานการผลิต',
          path: '/production/prd4020',
          roles: PrdReportRoles,
        },
        {
          type: 'item',
          code: 'prd4030',
          label: 'prd4030 รายงานการบรรจุ',
          path: '/production/prd4030',
          roles: PrdReportRoles,
        },
      ],
    },
  ],
}

const accountingMenus: MenuGroup = {
  type: 'group',
  label: 'บัญชี',
  module: 'บัญชี (Accounting)',
  leftSection: <IconReportSearch size="1rem" stroke={1.5} color="white" />,
  layoutType: 'single',
  sections: [
    {
      type: 'section',
      label: 'ข้อมูลหลัก',
      menus: [
        {
          type: 'item',
          code: 'ChartOfAccounts',
          label: 'ผังบัญชี',
          path: '/accounting/chart-of-accounts',
          roles: [
            Role.ADMIN,
            Role.ADMIN_CKD,
            Role.ACCOUNTING_SUPERVISOR,
            Role.ACCOUNTING_OFFICER,
          ],
        },
        {
          type: 'item',
          code: 'FiscalYear',
          label: 'ปีบัญชี',
          path: '/accounting/fiscal-year',
          roles: [
            Role.ADMIN,
            Role.ADMIN_CKD,
            Role.ACCOUNTING_SUPERVISOR,
            Role.ACCOUNTING_OFFICER,
          ],
        },
      ],
    },
    {
      type: 'section',
      label: 'รายการ',
      menus: [
        {
          type: 'item',
          code: 'JournalEntry',
          label: 'รายการบันทึกบัญชี',
          path: '/accounting/journal-entry',
          roles: [
            Role.ADMIN,
            Role.ADMIN_CKD,
            Role.ACCOUNTING_SUPERVISOR,
            Role.ACCOUNTING_OFFICER,
          ],
        },
      ],
    },
    {
      type: 'section',
      label: 'รายงาน',
      menus: [
        {
          type: 'item',
          code: 'TrialBalance',
          label: 'งบทดลอง',
          path: '/accounting/reports/trial-balance',
          roles: [
            Role.ADMIN,
            Role.ADMIN_CKD,
            Role.ACCOUNTING_SUPERVISOR,
            Role.ACCOUNTING_OFFICER,
          ],
        },
        {
          type: 'item',
          code: 'GeneralLedger',
          label: 'บัญชีแยกประเภท',
          path: '/accounting/reports/general-ledger',
          roles: [
            Role.ADMIN,
            Role.ADMIN_CKD,
            Role.ACCOUNTING_SUPERVISOR,
            Role.ACCOUNTING_OFFICER,
          ],
        },
      ],
    },
  ],
}

const settingsMenus: MenuGroup = {
  type: 'group',
  label: 'Settings',
  module: 'Settings',
  leftSection: <IconReportSearch size="1rem" stroke={1.5} color="white" />,
  sections: [
    {
      type: 'section',
      label: 'Master Data',
      menus: [
        {
          type: 'item',
          code: 'Item',
          label: 'Item',
          path: '/settings/items',
          roles: [Role.ADMIN, Role.ADMIN_CKD],
        },
        {
          type: 'item',
          code: 'Supplier',
          label: 'Supplier',
          path: '/settings/suppliers',
          roles: [Role.ADMIN, Role.ADMIN_CKD],
        },
        {
          type: 'item',
          code: 'Customer',
          label: 'Customer',
          path: '/settings/customers',
          roles: [Role.ADMIN, Role.ADMIN_CKD],
        },
        {
          type: 'item',
          code: 'Mold',
          label: 'Mold',
          path: '/settings/molds',
          roles: [Role.ADMIN_CKD, Role.ADMIN],
        },
        {
          type: 'item',
          code: 'Running_Number',
          label: 'Running Number',
          path: '/settings/running-numbers',
        },
        {
          type: 'item',
          code: 'Machine',
          label: 'Machine',
          path: '/settings/machines',
        },
        {
          type: 'item',
          code: 'Holiday',
          label: 'Holiday',
          path: '/settings/holidays',
          roles: [Role.ADMIN, Role.ADMIN_CKD],
        },
      ],
    },
    {
      type: 'section',
      label: 'Services',
      menus: [
        {
          type: 'item',
          code: 'Bplus_Sync',
          label: 'Bplus Sync',
          path: '/purchase/bplus-sync',
          roles: [Role.ADMIN],
        },
        {
          type: 'item',
          code: 'Screen_Capture',
          label: 'Screen Capture',
          path: '/settings/screen-capture',
          roles: [Role.ADMIN],
        },
        {
          type: 'item',
          code: 'Test_Result',
          label: 'Test Result',
          path: '/testrun',
          roles: [Role.ADMIN],
        },
      ],
    },
    {
      type: 'section',
      label: 'User Management',
      menus: [
        {
          type: 'item',
          code: 'User',
          label: 'User',
          path: '/settings/users',
          roles: [Role.ADMIN],
        },
        {
          type: 'item',
          code: 'Role',
          label: 'Role',
          path: '/settings/roles',
          roles: [Role.ADMIN],
        },
        {
          type: 'item',
          code: 'Group',
          label: 'Group',
          path: '/settings/groups',
          roles: [Role.ADMIN],
        },
      ],
    },
  ],
}

export const menus: Menus = hydrateAllowedRolesFromJson([
  saleMenus,
  planningMenus,
  purchaseMenus,
  warehouseMenus,
  qcMenus,
  productionMenus,
  accountingMenus,
  settingsMenus,
])
